import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router';
import { Users, Clock, Scale, Briefcase, MapPin, ArrowRight, Check } from 'lucide-react';

export const AboutSection = () => {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1.5 bg-[#22c55e]/10 text-[#22c55e] rounded-full text-sm font-semibold mb-4 uppercase tracking-wider">
              Über uns
            </span>
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0f172a] mb-6 tracking-tight leading-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Wer steckt hinter
              <br />
              <span className="text-[#22c55e]">Dein Gutachten Direkt?</span>
            </h2>
            
            <div className="space-y-4 text-gray-700 text-lg leading-relaxed mb-8">
              <p>
                Wir sind ein <strong>Kfz-Sachverständigenbüro mit Hauptsitz in Rellingen</strong> (Kreis Pinneberg). 
                Unser Team besteht aus <strong>mehreren erfahrenen Sachverständigen</strong> mit über <strong>10 Jahren Erfahrung</strong> in der Kfz-Schadensbewertung.
              </p>
              <p>
                Als <strong>unabhängige Gutachter</strong> arbeiten wir ausschließlich für Sie – nicht für Versicherungen oder Werkstätten. 
                Unsere Gutachten sind <strong>gerichtsfest</strong> und wir arbeiten eng mit <strong>Fachanwälten für Verkehrsrecht</strong> zusammen.
              </p>
            </div>

            {/* Key Facts */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#22c55e]/10 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#22c55e]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0f172a]">10+ Jahre</p>
                  <p className="text-sm text-gray-500">Erfahrung</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#22c55e]/10 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#22c55e]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0f172a]">Team</p>
                  <p className="text-sm text-gray-500">Mehrere Sachverständige</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#22c55e]/10 rounded-xl flex items-center justify-center">
                  <Scale className="w-5 h-5 text-[#22c55e]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0f172a]">Gerichtsfest</p>
                  <p className="text-sm text-gray-500">Rechtssichere Gutachten</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#22c55e]/10 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-[#22c55e]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0f172a]">Anwälte</p>
                  <p className="text-sm text-gray-500">Enge Zusammenarbeit</p>
                </div>
              </div>
            </div>

            <Link 
              to="/ueber-uns" 
              className="inline-flex items-center gap-2 text-[#22c55e] font-semibold hover:gap-4 transition-all group"
            >
              Mehr über uns erfahren
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          {/* Right: What's included */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-gray-50 rounded-3xl p-8 lg:p-10 border border-gray-100">
              <h3 className="text-2xl font-bold text-[#0f172a] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Was beinhaltet unser Gutachten?
              </h3>
              
              <div className="space-y-4">
                {[
                  { title: 'Reparaturkostenkalkulation', desc: 'Detaillierte Berechnung nach Herstellervorgaben' },
                  { title: 'Wertminderung', desc: 'Merkantiler Wertverlust Ihres Fahrzeugs' },
                  { title: 'Nutzungsausfall', desc: 'Entschädigung für Ausfalltage' },
                  { title: 'Wiederbeschaffungswert', desc: 'Marktwert bei Totalschaden' },
                  { title: 'Restwertermittlung', desc: 'Verkaufswert des beschädigten Fahrzeugs' },
                  { title: 'Fotodokumentation', desc: 'Professionelle Beweissicherung' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#22c55e] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0f172a]">{item.title}</p>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-[#22c55e]/10 border border-[#22c55e]/20 rounded-xl">
                <p className="text-sm text-gray-700">
                  <strong className="text-[#0f172a]">Wichtig:</strong> Ein Werkstatt-Kostenvoranschlag enthält nur Reparaturkosten. 
                  Wertminderung und Nutzungsausfall fehlen dort komplett!
                </p>
              </div>
            </div>

            {/* Freie Gutachterwahl Badge */}
            <div className="mt-6 p-4 bg-[#0f172a] rounded-2xl flex items-center gap-4">
              <div className="w-12 h-12 bg-[#22c55e] rounded-xl flex items-center justify-center flex-shrink-0">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold">Freie Gutachterwahl</p>
                <p className="text-gray-400 text-sm">Bei unverschuldetem Unfall dürfen Sie Ihren Gutachter frei wählen. Die Kosten trägt die gegnerische Versicherung.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
